"use client";

import Image from "next/image";
import { useState } from "react";
import { Check, Send, TicketPercent } from "lucide-react";
import { DeviceStage, MacbookFrame } from "./device-frames";
import { useSelfDemo } from "./use-self-demo";
import { useTranslation } from "@/lib/hooks/use-translation";

/**
 * Card 2's screen: the CRM campaign builder on a MacBook.
 *
 * Two moves, both the point of the card — pick a customer group and the coupon
 * rewrites itself for that group; hit ส่ง and it goes out over LINE OA to
 * exactly that many people. Nothing leaves the browser: the "send" only flips
 * local state.
 *
 * The demo plays those two moves and nothing else: ลูกค้าประจำ, then ลูกค้าใหม่,
 * then send. It deliberately leaves ห่างหายไป alone — three groups in a row is
 * a tour of the control, where two is the argument (same coupon, different
 * group, different message) followed by the payoff.
 *
 * The screen is laid out as the two halves of the job, each with a heading:
 * ใครจะได้รับ on the left, and on the right an actual LINE thread showing what
 * lands on the customer's phone — shop avatar, incoming bubble, coupon. At
 * mock-up scale a reader has about a second to work out what they're looking
 * at, so the chat has to look like a chat, and the send button carries the
 * headcount rather than a generic label.
 *
 * Palette: brand blue #0060A8 for chrome and the chosen group; yellow #FFCC33
 * is the money — the coupon itself, and nothing else. Yellow only ever sits
 * under deep-blue ink.
 */

type Segment = {
  /** Also the key into `home.mocks.crm.segments`, which carries this group's
      label, the rule behind it, its offer and the message body. */
  id: string;
  reach: string;
};

const SEGMENTS: Segment[] = [
  {
    id: "regular",
    reach: "1,248",
  },
  {
    id: "new",
    reach: "320",
  },
  {
    id: "lapsed",
    reach: "486",
  },
];

export default function CrmMock({ active }: { active?: boolean }) {
  const { t } = useTranslation();
  const [at, setAt] = useState(0);
  const [sent, setSent] = useState<string | null>(null);
  /** The group that was just pressed. `n` only exists to re-key the ripple, so
      pressing the same group twice replays it instead of doing nothing. */
  const [tap, setTap] = useState<{ at: number; n: number } | null>(null);
  const segment = SEGMENTS[at];

  // ลูกค้าประจำ → ลูกค้าใหม่ → send. The send is what produces the customer
  // notification, and that payoff is the whole argument of the card, so it
  // shouldn't depend on being discovered. Picking any group afterwards clears
  // it, which hands the send back.
  // Loops: the three beats already close their own circle — `show(0)` clears
  // the sent state — so the next pass starts from the same screen the first
  // one did, and a reader arriving late still sees the send happen rather than
  // a finished still. It stops for good the moment they pick a group.
  const { ref: screenRef, cancel } = useSelfDemo<HTMLDivElement>(
    [() => show(0), () => show(1), () => setSent(SEGMENTS[1].id)],
    { active, loop: true },
  );

  function show(i: number) {
    setAt(i);
    setSent(null);
    // The first step of the demo re-presses the group that is already chosen;
    // without a ripple that beat would look like nothing happened at all.
    setTap((prev) => ({ at: i, n: (prev?.n ?? 0) + 1 }));
  }

  function pick(i: number) {
    cancel();
    show(i);
  }

  return (
    <DeviceStage>
      <MacbookFrame>
        <div ref={screenRef} className="flex h-full flex-col bg-[#F4F8FC]">
          {/* App chrome, same brand bar as the other two screens. */}
          <div className="flex items-center justify-between bg-[linear-gradient(115deg,#00518D_0%,#0060A8_55%,#0A70BC_100%)] px-[0.6em] py-[0.4em]">
            <span className="flex items-center gap-[0.4em]">
              <Image
                src="/v2/logo.webp"
                alt=""
                width={48}
                height={48}
                className="size-[1em] rounded-full bg-white/90 p-[0.1em]"
              />
              <span className="text-[0.7em] font-semibold text-white">
                {t("home.mocks.crm.campaignTitle")}
              </span>
            </span>
            <span className="rounded-full bg-white/15 px-[0.5em] py-[0.15em] text-[0.5em] font-medium text-white">
              CRM
            </span>
          </div>

          <div className="flex min-h-0 flex-1">
            {/* Who gets it */}
            <div className="flex w-[42%] flex-col gap-[0.28em] border-r border-[#0060A8]/10 p-[0.45em]">
              <p className="text-[0.5em] font-semibold text-gray-400">
                {t("home.mocks.crm.audience")}
              </p>
              {SEGMENTS.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pick(i)}
                  aria-pressed={i === at}
                  className={`relative flex items-center gap-[0.3em] rounded-[0.35em] px-[0.35em] py-[0.28em] text-left transition-colors ${
                    i === at
                      ? "bg-[#0060A8] shadow-[0_0.15em_0.4em_-0.12em_rgba(0,96,168,0.6)]"
                      : "bg-white shadow-[0_0.1em_0.3em_-0.12em_rgba(0,96,168,0.25)] hover:bg-[#E8F2FA]"
                  }`}
                >
                  {tap?.at === i && (
                    <span
                      // New element per press, which is what replays the
                      // keyframe — see globals.css, ps-mock-press.
                      key={tap.n}
                      aria-hidden
                      className="ps-mock-press pointer-events-none absolute inset-0 rounded-[0.35em]"
                    />
                  )}

                  {/* Radio dot: says "pick one" without needing a caption. */}
                  <span
                    className={`grid size-[0.7em] shrink-0 place-items-center rounded-full border-[0.09em] ${
                      i === at ? "border-white bg-white" : "border-[#0060A8]/35"
                    }`}
                  >
                    {i === at && (
                      <span className="size-[0.28em] rounded-full bg-[#0060A8]" />
                    )}
                  </span>
                  <span className="min-w-0">
                    <span
                      className={`block truncate text-[0.55em] font-semibold ${i === at ? "text-white" : "text-[#00518D]"}`}
                    >
                      {t(`home.mocks.crm.segments.${s.id}.label`)}
                    </span>
                    <span
                      className={`block truncate text-[0.45em] ${i === at ? "text-white/70" : "text-gray-400"}`}
                    >
                      {t(`home.mocks.crm.segments.${s.id}.rule`)} · {s.reach}{" "}
                      {t("home.mocks.crm.people")}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            {/* What they get */}
            <div className="flex min-w-0 flex-1 flex-col gap-[0.28em] p-[0.45em]">
              <p className="flex items-center gap-[0.3em] text-[0.5em] font-semibold text-gray-400">
                {t("home.mocks.crm.messageOn")}
                <span className="rounded-[0.2em] bg-[#06C755] px-[0.3em] py-[0.05em] text-[0.85em] font-bold text-white">
                  LINE
                </span>
              </p>

              {/* A thread, not a panel: avatar on the left and an incoming
                  bubble, which is how the customer actually receives it. */}
              <div className="flex min-h-0 flex-1 flex-col justify-end rounded-[0.35em] bg-[#A8C4E0]/25 p-[0.35em]">
                <div className="flex gap-[0.28em]">
                  <Image
                    src="/v2/logo.webp"
                    alt=""
                    width={48}
                    height={48}
                    className="size-[0.9em] shrink-0 rounded-full bg-white p-[0.08em]"
                  />
                  <div
                    // key restarts the slide-in whenever the coupon changes.
                    key={segment.id}
                    className="ps-mock-bubble min-w-0 rounded-[0.4em] rounded-tl-[0.12em] bg-white p-[0.3em] shadow-[0_0.15em_0.4em_-0.15em_rgba(0,40,80,0.28)]"
                  >
                    <div className="rounded-[0.3em] bg-[linear-gradient(135deg,#FFD24D_0%,#FFC81F_100%)] px-[0.35em] py-[0.28em]">
                      <span className="flex items-center gap-[0.25em]">
                        <TicketPercent
                          aria-hidden
                          className="size-[0.65em] shrink-0 text-[#00518D]"
                          strokeWidth={2.4}
                        />
                        <span className="truncate text-[0.62em] font-bold text-[#00518D]">
                          {t(`home.mocks.crm.segments.${segment.id}.offer`)}
                        </span>
                      </span>
                      <span className="mt-[0.2em] block border-t border-dashed border-[#00518D]/35 pt-[0.2em] text-[0.42em] font-medium text-[#00518D]/75">
                        {t("home.mocks.crm.redeem")}
                      </span>
                    </div>
                    <p className="pt-[0.25em] text-[0.45em]/[1.5] text-gray-600">
                      {t(`home.mocks.crm.segments.${segment.id}.detail`)}
                    </p>
                  </div>
                </div>
              </div>

              {sent === segment.id ? (
                <p className="ps-mock-pop flex items-center justify-center gap-[0.3em] rounded-full bg-[#E6F4EA] py-[0.25em] text-[0.5em] font-semibold text-[#1B7F3B]">
                  <Check aria-hidden className="size-[1em]" strokeWidth={3.5} />
                  {t("home.mocks.crm.sentTo", { count: segment.reach })}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    cancel();
                    setSent(segment.id);
                  }}
                  className="flex items-center justify-center gap-[0.3em] rounded-full bg-[#0060A8] py-[0.25em] text-[0.5em] font-semibold text-white shadow-[0_0.15em_0.4em_-0.12em_rgba(0,96,168,0.6)] transition-colors hover:bg-[#00518D]"
                >
                  <Send aria-hidden className="size-[1em]" />
                  {t("home.mocks.crm.sendTo", { count: segment.reach })}
                </button>
              )}
            </div>
          </div>
        </div>
      </MacbookFrame>

      {/* The customer's side of the send, floated over the laptop the way the
          hero's notification cards sit over its devices. It only exists once a
          broadcast has gone out, and its keyframe carries a delay so it reads
          as the consequence rather than part of the same beat. */}
      {sent === segment.id && (
        <div
          key={sent}
          // text-[1.3em] is the card's only size knob: everything inside it
          // is em-based, so one number scales the whole notification without
          // touching the laptop it floats over.
          className="ps-mock-notify ps-mock-notify-late absolute -bottom-[9%] -left-[5%] flex items-center gap-[0.55em] text-[1.3em] rounded-[0.9em] border border-black/5 bg-white py-[0.4em] pl-[0.4em] pr-[0.9em] shadow-[0_0.9em_2em_-0.5em_rgba(15,23,42,0.45)]"
        >
          <Image
            src="/v2/customer-coupon.webp"
            alt=""
            width={220}
            height={220}
            className="size-[2.8em] shrink-0 rounded-[0.6em] object-cover"
          />
          <span>
            <span className="flex items-center gap-[0.35em] text-[0.68em] font-semibold text-gray-900">
              {t("home.mocks.crm.gotCoupon")}
              <span className="rounded-full bg-[#FFCC33] px-[0.5em] py-[0.05em] text-[0.75em] font-bold text-[#00518D]">
                {t("home.mocks.crm.new")}
              </span>
            </span>
            <span className="block text-[0.58em] text-gray-500">
              {t("home.mocks.crm.who")}
            </span>
          </span>
        </div>
      )}
    </DeviceStage>
  );
}
